import { useEffect, useMemo, useState } from 'react';
import { builder, BuilderContent } from '@builder.io/sdk';
import { ArticlePreview, BuilderArticlePageReference } from '@/types/article';

const ARTICLE_FIELDS =
  'data.title,data.url,data.heroImage,data.category,data.tags,data.featured,lastUpdated,firstPublished';

function initBuilderSdk(): boolean {
  if (!process.env.NEXT_PUBLIC_BUILDER_API_KEY) return false;
  builder.init(process.env.NEXT_PUBLIC_BUILDER_API_KEY);
  return true;
}

function mapBuilderResults(results: BuilderContent[]): ArticlePreview[] {
  return results
    .filter((article) => article.data)
    .map((article) => ({
      ...article.data,
      lastUpdated: article.lastUpdated,
      firstPublished: article.firstPublished,
    })) as ArticlePreview[];
}

function hasCategoryData(article: ArticlePreview): boolean {
  return !!(article.category?.value && (article.category.value as BuilderContent).data);
}

interface UseBuilderArticlesOptions {
  autoSelectLatest: boolean;
  limit: number;
  latestArticles: ArticlePreview[] | undefined;
  articleInputs: { article?: BuilderArticlePageReference }[];
}

/**
 * Resolves articles for Builder components that support both auto-select and
 * manual modes. Handles two Builder SDK editor limitations:
 *
 * 1. Server-fetched state data (featuredArticles) isn't preserved in the
 *    editor's client-side state, so auto-select mode needs a client-side
 *    fallback fetch.
 * 2. Nested references (e.g. category within article) aren't resolved in
 *    the editor's content state, so manual articles need client-side
 *    enrichment to display category tags.
 */
export function useBuilderArticles({
  autoSelectLatest,
  limit,
  latestArticles,
  articleInputs,
}: UseBuilderArticlesOptions): ArticlePreview[] {
  const articleModel = articleInputs[0]?.article?.model ?? 'article';

  // --- Auto-select fallback ---
  const [fallbackArticles, setFallbackArticles] = useState<ArticlePreview[]>([]);

  useEffect(() => {
    if (!autoSelectLatest || (latestArticles && latestArticles.length > 0) || fallbackArticles.length > 0) return;
    if (!initBuilderSdk()) return;

    builder
      .getAll(articleModel, {
        fields: ARTICLE_FIELDS,
        limit,
        sort: { firstPublished: -1 },
        options: { noTargeting: true, includeRefs: true },
      })
      .then((results) => setFallbackArticles(mapBuilderResults(results)));
  }, [autoSelectLatest, latestArticles, limit, fallbackArticles.length, articleModel]);

  // --- Manual article mapping ---
  const mappedArticles = useMemo(
    () => articleInputs.map((a) => a.article?.value?.data).filter((data) => !!data),
    [articleInputs]
  );

  // --- Manual article category enrichment ---
  const [enrichedManualArticles, setEnrichedManualArticles] = useState<ArticlePreview[]>([]);
  const manualArticleUrls = useMemo(() => mappedArticles.map((a) => a.url).filter(Boolean), [mappedArticles]);

  useEffect(() => {
    if (autoSelectLatest || manualArticleUrls.length === 0) return;

    const hasUnresolvedCategories = mappedArticles.some((a) => a.category?.id && !hasCategoryData(a as ArticlePreview));
    if (!hasUnresolvedCategories) return;
    if (!initBuilderSdk()) return;

    // Omit `fields` so includeRefs can fully populate nested reference values
    builder
      .getAll(articleModel, {
        query: { 'data.url': { $in: manualArticleUrls } },
        options: { noTargeting: true, includeRefs: true },
      })
      .then(async (results) => {
        let enriched = mapBuilderResults(results);

        // Fallback: if includeRefs didn't resolve categories, fetch them directly
        const unresolvedIds = [
          ...new Set(enriched.filter((a) => a.category?.id && !hasCategoryData(a)).map((a) => a.category!.id)),
        ];

        if (unresolvedIds.length > 0) {
          try {
            const categoryResults = await builder.getAll('category', {
              limit: unresolvedIds.length,
              query: { id: { $in: unresolvedIds } },
              options: { noTargeting: true },
            });
            const categoryMap = new Map<string, BuilderContent>(
              categoryResults.filter((c) => c.id).map((c) => [c.id!, c])
            );
            enriched = enriched.map((article) => {
              const catId = article.category?.id;
              if (catId && !hasCategoryData(article) && categoryMap.has(catId)) {
                return {
                  ...article,
                  category: { ...article.category!, value: categoryMap.get(catId)! },
                };
              }
              return article;
            }) as ArticlePreview[];
          } catch {
            // Category fetch failed; continue with partially enriched data
          }
        }

        const enrichedMap = new Map(enriched.map((a) => [a.url, a]));
        setEnrichedManualArticles(
          manualArticleUrls.map((url) => enrichedMap.get(url)).filter(Boolean) as ArticlePreview[]
        );
      });
  }, [autoSelectLatest, manualArticleUrls, mappedArticles, articleModel]);

  // --- Resolve final article list ---
  if (autoSelectLatest) {
    const resolved = latestArticles?.length ? latestArticles : fallbackArticles;
    return resolved.slice(0, limit);
  }

  return enrichedManualArticles.length ? enrichedManualArticles : mappedArticles;
}
